import { setTaskTimingEnd, setTaskTimingStart, type TaskScheduleValue, type TaskStatusValue } from "@/lib/taskTiming";
import { formatDateKey } from "@/lib/time";
import { advanceTaskExecution, resolveTaskExecutionTimestamp } from "@shared/taskLifecycle";

export function transitionTaskStatus(
  value: TaskStatusValue,
  schedule: TaskScheduleValue,
  status: TaskStatusValue["status"],
  now: Date,
): TaskStatusValue {
  if (status !== "done") {
    return { status, startedAt: null, completedAt: null, timing: emptyTaskTiming() };
  }

  const scheduledStart = schedule.timing.startDate && schedule.timing.startTime
    ? combineDateTime(schedule.timing.startDate, schedule.timing.startTime)
    : null;
  const when = resolveTaskExecutionTimestamp({ startedAt: value.startedAt }, now, scheduledStart);
  const patch = advanceTaskExecution(value, when);
  const startedAt = "startedAt" in patch ? patch.startedAt ?? null : value.startedAt;
  const completedAt = "completedAt" in patch ? patch.completedAt ?? null : value.completedAt;
  return { status: "done", startedAt, completedAt, timing: timingFromDates(startedAt, completedAt) };
}

export function setTaskStatusNow(value: TaskStatusValue, now: Date): TaskStatusValue {
  const date = formatDateKey(now);
  const time = formatTimeValue(now.getHours(), now.getMinutes());
  const timing = !value.timing.startTime
    ? setTaskTimingStart(value.timing, date, time)
    : setTaskTimingEnd(value.timing, date, time);
  return {
    status: "done",
    startedAt: timing.startDate && timing.startTime ? combineDateTime(timing.startDate, timing.startTime) : null,
    completedAt: timing.endDate && timing.endTime ? combineDateTime(timing.endDate, timing.endTime) : null,
    timing,
  };
}

export function timingFromDates(startedAt: Date | null, completedAt: Date | null) {
  const duration = startedAt && completedAt
    ? Math.max(0, Math.round((completedAt.getTime() - startedAt.getTime()) / 60_000))
    : null;
  return {
    startDate: startedAt ? formatDateKey(startedAt) : "",
    endDate: completedAt ? formatDateKey(completedAt) : startedAt ? formatDateKey(startedAt) : "",
    startTime: startedAt ? formatTimeValue(startedAt.getHours(), startedAt.getMinutes()) : "",
    endTime: completedAt ? formatTimeValue(completedAt.getHours(), completedAt.getMinutes()) : "",
    durationMinutes: duration == null ? "" : String(duration),
  };
}

export function emptyTaskTiming() {
  return { startDate: "", endDate: "", startTime: "", endTime: "", durationMinutes: "" };
}

function combineDateTime(date: string, time: string) {
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
}

function formatTimeValue(hours: number, minutes: number) {
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}
